import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarCheck,
  Check,
  Copy,
  Loader2,
  Mail,
  PhoneCall,
  TriangleAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getListCalendarConnectionsQueryKey,
  useListCalendarConnections,
} from '@biasafe.queries/calendar-connections/calendar-connections';
import {
  getGetMyMailboxChannelQueryKey,
  useGetMyMailboxChannel,
} from '@biasafe.queries/mailbox-channel/mailbox-channel';
import {
  getGetPhoneStatusQueryKey,
  useGetPhoneStatus,
} from '@biasafe.queries/phone-status/phone-status';
import { getGetProfileQueryKey } from '@biasafe.queries/user/user';
import { useMarkConnectToolsDone } from '@biasafe.queries/user-connect-tools-done/user-connect-tools-done';
import { useSignInProvider } from './useSignInProvider';
import type { ReactNode } from 'react';
import type { UserProfile } from '@biasafe.models';
import type {
  SignupProvider,
  ToolView,
} from '@/features/Integrations/connectToolsState';
import {
  adminRequestText,
  calendarToolView,
  isSettled,
  mailboxToolView,
  phoneToolView,
} from '@/features/Integrations/connectToolsState';
import { useConnectCalendar } from '@/features/Settings/calendar/useConnectCalendar';
import { useConnectMailbox } from '@/features/Clients/mailbox/useConnectMailbox';
import { usePhoneConnect } from '@/features/Phone/usePhoneConnect';
import { fetchMfaStatus } from '@/features/Auth/gcp/emailMfa';
import { securitySetupUrl } from '@/features/Auth/mfaReturn';
import { useIsOrgAdmin } from '@/entities/user/store';
import { PROFILE_STALE_TIME } from '@/shared/config/queryDefaults';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/**
 * The "Connect your tools" step, offered once right after workspace setup.
 *
 * WHO SEES IT is the server's call, through `showConnectTools` on the profile:
 * an account created after the step shipped that has not finished or skipped
 * it. Every existing account was backfilled when the column was added, so
 * production behaves as before for everyone who was already here. It is on the
 * account and not in localStorage, so a skip on one computer is a skip
 * everywhere. It waits behind the welcome tour, which opens first for a new
 * admin; the two never stack.
 *
 * EVERYTHING IT CONNECTS IS THE EXISTING FLOW. Mailbox, calendar and Teams
 * Phone run through the same hooks the Settings and Integrations cards use
 * (`useConnectMailbox`, `useConnectCalendar`, `usePhoneConnect`), and what each
 * row says comes from `connectToolsState.ts`, which reads the same tile states
 * as the Integrations page. There is no second copy of an OAuth flow here.
 *
 * ONE CONSENT, OR TWO? The backend keeps the mailbox and the calendar apart: a
 * separate authorize endpoint, state namespace, redirect URI and code exchange
 * for each (`v1/mailbox`, `v1/calendar`). An authorization code is single use
 * and bound to one redirect URI, so a single consent cannot be exchanged for
 * both. The step therefore runs the two connects in sequence: one primary button
 * starts the first outstanding one, and the next press does the next. It does
 * NOT chain them from one click, because the second popup would open long after
 * the click that allows it and every browser blocks that. Each row has its own
 * status and its own Retry. Google's second consent only lists the calendar
 * (`include_granted_scopes`), so the person approves one short screen each.
 *
 * TEAMS PHONE IS NEVER STARTED FOR THEM. It is a tenant-wide Microsoft approval:
 * the button is drawn only for an organization admin, the server verifies the
 * Microsoft sign-in carries a tenant administrator role before it binds
 * anything, and everyone else sees "Ask your administrator" with a request to
 * copy. The primary button never includes it.
 *
 * TWO-FACTOR. With MFA_ENFORCEMENT_MODE=before_regulated_action an unenrolled
 * user reaches the dashboard but the calendar, mailbox and phone routers refuse
 * EVERY call (reads included) with 403 AU403MFAA. The step asks `/auth/mfa/status`
 * first (an open route) and, for an unenrolled user, fires none of those calls
 * and shows a link to setup instead. A 403 mid-step would send them to
 * /security-setup by itself (see `mfaReturn.isPassiveRead`); checking first
 * keeps that choice theirs. Dismissing in that state is for this visit only, so
 * the step is offered again once they have a second factor.
 */
export function ConnectToolsStep({
  profile,
}: {
  profile: UserProfile | undefined;
}) {
  const eligible =
    profile?.showConnectTools === true && profile.needsOnboarding !== true;
  // The tour opens first for a new admin. Waiting on the same flag the tour
  // reads means this appears the moment the tour's dismissal reaches the
  // cached profile, and never over it.
  const tourPending =
    profile?.orgRole === 'admin' && profile.showWelcomeTour === true;
  const [dismissed, setDismissed] = useState(false);

  if (!eligible || tourPending || dismissed) return null;
  return <ConnectToolsDialog onDismiss={() => setDismissed(true)} />;
}

type ToolId = 'mailbox' | 'calendar' | 'phone';

const MAIL_LABEL = { GOOGLE: 'Gmail', MICROSOFT: 'Outlook' } as const;
const CALENDAR_LABEL = {
  GOOGLE: 'Google Calendar',
  MICROSOFT: 'Outlook Calendar',
} as const;

function ConnectToolsDialog({ onDismiss }: { onDismiss: () => void }) {
  const queryClient = useQueryClient();
  const isOrgAdmin = useIsOrgAdmin();
  const signedInWith = useSignInProvider();
  const [chosen, setChosen] = useState<SignupProvider | null>(null);
  const provider = signedInWith ?? chosen;
  const [skipped, setSkipped] = useState<Record<ToolId, boolean>>({
    mailbox: false,
    calendar: false,
    phone: false,
  });
  // Set when a connect attempt on Teams Phone fails: the usual reason is that
  // the person is not a Microsoft 365 global administrator.
  const [phoneFailed, setPhoneFailed] = useState(false);

  // Asked FIRST, and the answer decides whether anything below is read. An
  // unreadable answer does not block: the calls then report for themselves.
  const mfa = useQuery({
    queryKey: ['auth', 'mfa', 'status'],
    queryFn: fetchMfaStatus,
    staleTime: PROFILE_STALE_TIME,
    retry: false,
  });
  const blocked = mfa.data !== undefined && !mfa.data.enrolled;
  const ready = !mfa.isLoading && !blocked;

  const calendars = useListCalendarConnections({
    query: { enabled: ready, queryKey: getListCalendarConnectionsQueryKey() },
  });
  const mailbox = useGetMyMailboxChannel({
    query: { enabled: ready, queryKey: getGetMyMailboxChannelQueryKey() },
  });
  const phone = useGetPhoneStatus({
    query: { enabled: ready, queryKey: getGetPhoneStatusQueryKey() },
  });

  const mail = useConnectMailbox();
  const calendar = useConnectCalendar();
  const phoneConnect = usePhoneConnect();
  const markDone = useMarkConnectToolsDone({
    mutation: {
      // The response is the profile with the flag already cleared.
      onSuccess: (fresh) => {
        queryClient.setQueryData(getGetProfileQueryKey(), fresh);
      },
    },
  });

  const busy =
    mail.busy !== null || calendar.pending !== null || phoneConnect.busy;

  const mailView: ToolView | null = provider
    ? mailboxToolView(mailbox, provider)
    : null;
  const calendarView: ToolView | null = provider
    ? calendarToolView(calendars, provider === 'GOOGLE' ? 'GOOGLE' : 'OUTLOOK')
    : null;
  const basePhoneView = phoneToolView(phone, isOrgAdmin);
  // A failed attempt moves the row to "ask your administrator": whoever it
  // was could not prove they are a tenant administrator.
  const phoneView: ToolView | null =
    provider === 'MICROSOFT'
      ? phoneFailed && basePhoneView.status !== 'connected'
        ? { status: 'ask_admin', detail: null, action: null }
        : basePhoneView
      : null;

  const rows: Array<{ id: ToolId; view: ToolView | null }> = [
    { id: 'mailbox', view: mailView },
    { id: 'calendar', view: calendarView },
    { id: 'phone', view: phoneView },
  ];
  const shown = rows.filter(
    (r): r is { id: ToolId; view: ToolView } => r.view !== null,
  );
  const allSettled =
    shown.length > 0 &&
    shown.every((r) => isSettled(r.view.status, skipped[r.id]));

  // The primary button: the next of mailbox and calendar still to be done.
  const outstanding = shown.filter(
    (r) =>
      (r.id === 'mailbox' || r.id === 'calendar') &&
      !skipped[r.id] &&
      (r.view.action === 'connect' || r.view.action === 'reconnect'),
  );

  const runMailbox = () => {
    if (provider) void mail.connect(provider);
  };
  const runCalendar = () => {
    if (provider)
      void calendar.connect(provider === 'GOOGLE' ? 'GOOGLE' : 'OUTLOOK');
  };
  const runPhone = async () => {
    setPhoneFailed(false);
    const outcome = await phoneConnect.connect();
    if (outcome === 'failed') setPhoneFailed(true);
  };
  const retryRead = (id: ToolId) => {
    if (id === 'mailbox') void mailbox.refetch();
    else if (id === 'calendar') void calendars.refetch();
    else void phone.refetch();
  };

  const primary = (() => {
    if (outstanding.length === 0) return null;
    const first = outstanding[0];
    const both = outstanding.length === 2;
    return {
      label: both
        ? 'Connect mailbox and calendar'
        : first.id === 'mailbox'
          ? `Connect ${MAIL_LABEL[provider ?? 'GOOGLE']}`
          : `Connect ${CALENDAR_LABEL[provider ?? 'GOOGLE']}`,
      run: first.id === 'mailbox' ? runMailbox : runCalendar,
    };
  })();

  /** Every way out that is not "I am still setting up" counts as done. */
  const finish = () => {
    onDismiss();
    markDone.mutate();
  };
  const close = () => {
    if (busy) return;
    // Not yet able to connect anything (no second factor): leave for this
    // visit only, so the step is still offered once they can.
    if (blocked) onDismiss();
    else finish();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent
        className="max-h-[calc(100dvh-2rem)] gap-0 overflow-y-auto p-0 sm:max-w-xl"
        data-testid="connect-tools-step"
      >
        <div className="px-6 pt-6 pb-2 sm:px-8">
          <p className="text-xs font-medium tracking-wide text-primary uppercase">
            Optional
          </p>
          <DialogTitle className="mt-1.5 text-xl leading-tight text-foreground">
            Connect your tools
          </DialogTitle>
          <DialogDescription className="mt-2 text-sm leading-relaxed">
            Connect your mailbox and calendar so client emails and meetings are
            filed for you. You can skip any of it and come back later from
            Integrations.
          </DialogDescription>
        </div>

        <div className="space-y-3 px-6 py-4 sm:px-8">
          {mfa.isLoading && (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}

          {blocked && (
            <div
              role="status"
              className="space-y-3 rounded-md border bg-muted/40 p-4"
            >
              <p className="flex items-start gap-2 text-sm font-medium">
                <TriangleAlert
                  className="mt-0.5 size-4 shrink-0 text-destructive"
                  aria-hidden="true"
                />
                Needs attention: set up two-factor sign-in first
              </p>
              <p className="text-sm text-muted-foreground">
                Your mailbox, calendar and phone connections open once
                two-factor sign-in is on. Nothing has been connected, and you
                will be offered this again afterwards.
              </p>
              <Button asChild size="sm">
                <a href={securitySetupUrl(window.location.pathname)}>
                  Set up two-factor sign-in
                </a>
              </Button>
            </div>
          )}

          {ready && !provider && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Which do you use for email?</p>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => setChosen('GOOGLE')}>
                  Google: Gmail and Calendar
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setChosen('MICROSOFT')}
                >
                  Microsoft: Outlook and Calendar
                </Button>
              </div>
            </div>
          )}

          {ready &&
            shown.map(({ id, view }) => {
              const isSkipped = skipped[id];
              const toggleSkip = () =>
                setSkipped((s) => ({ ...s, [id]: !s[id] }));
              const title =
                id === 'mailbox'
                  ? `${MAIL_LABEL[provider ?? 'GOOGLE']} mailbox`
                  : id === 'calendar'
                    ? CALENDAR_LABEL[provider ?? 'GOOGLE']
                    : 'Microsoft Teams Phone';
              const icon =
                id === 'mailbox' ? (
                  <Mail className="size-4" aria-hidden="true" />
                ) : id === 'calendar' ? (
                  <CalendarCheck className="size-4" aria-hidden="true" />
                ) : (
                  <PhoneCall className="size-4" aria-hidden="true" />
                );
              const error =
                id === 'mailbox'
                  ? mail.error
                  : id === 'calendar'
                    ? calendar.error
                    : phoneConnect.error;
              const rowBusy =
                id === 'mailbox'
                  ? mail.busy !== null
                  : id === 'calendar'
                    ? calendar.pending !== null
                    : phoneConnect.busy;
              const act = () => {
                if (view.action === 'retry') retryRead(id);
                else if (id === 'mailbox') runMailbox();
                else if (id === 'calendar') runCalendar();
                else void runPhone();
              };
              return (
                <ToolRow
                  key={id}
                  icon={icon}
                  title={title}
                  view={view}
                  skipped={isSkipped}
                  error={error}
                  busy={rowBusy}
                  disabled={busy}
                  onAct={act}
                  onToggleSkip={toggleSkip}
                >
                  {id === 'phone' && (
                    <AdminRequest
                      show={view.status === 'ask_admin' || phoneFailed}
                      offerCopy={
                        view.status === 'todo' || view.status === 'attention'
                      }
                    />
                  )}
                </ToolRow>
              );
            })}

          {primary && !blocked && (
            <p className="text-xs text-muted-foreground">
              Each one opens its own sign-in window, one after the other.
            </p>
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-border px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <Button variant="ghost" size="sm" disabled={busy} onClick={close}>
            {blocked ? 'Not now' : 'Skip all'}
          </Button>
          {!blocked && (
            <div className="flex items-center gap-2">
              {primary && (
                <Button size="sm" disabled={busy} onClick={primary.run}>
                  {busy && (
                    <Loader2
                      className="size-4 animate-spin"
                      aria-hidden="true"
                    />
                  )}
                  {primary.label}
                </Button>
              )}
              <Button
                size="sm"
                variant={primary ? 'outline' : 'default'}
                disabled={busy || !allSettled}
                onClick={finish}
              >
                Done
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_TEXT: Record<ToolView['status'], string> = {
  loading: 'Checking',
  connected: 'Connected',
  unconfigured: 'Not set up on this environment',
  todo: 'Not connected',
  attention: 'Needs attention',
  ask_admin: 'Ask your administrator',
};

function ToolRow({
  icon,
  title,
  view,
  skipped,
  error,
  busy,
  disabled,
  onAct,
  onToggleSkip,
  children,
}: {
  icon: ReactNode;
  title: string;
  view: ToolView;
  skipped: boolean;
  error: string | null;
  busy: boolean;
  disabled: boolean;
  onAct: () => void;
  onToggleSkip: () => void;
  children?: ReactNode;
}) {
  const canSkip = view.status === 'todo' || view.status === 'attention';
  const label =
    view.action === 'reconnect'
      ? 'Reconnect'
      : view.action === 'retry'
        ? 'Try again'
        : 'Connect';
  return (
    <div
      className="space-y-2 rounded-md border p-4"
      data-testid={`connect-tools-row-${title}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            {icon}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium">{title}</p>
            <p
              className={cn(
                'flex items-center gap-1 text-[13px]',
                view.status === 'attention'
                  ? 'text-destructive'
                  : 'text-muted-foreground',
              )}
            >
              {view.status === 'connected' && (
                <Check className="size-3.5" aria-hidden="true" />
              )}
              {skipped ? 'Skipped' : STATUS_TEXT[view.status]}
            </p>
            {!skipped && view.detail && view.status !== 'unconfigured' && (
              <p className="mt-1 text-[13px] text-muted-foreground">
                {view.detail}
              </p>
            )}
          </div>
        </div>
        {/* No button at all where the environment lacks the credential, and
            none where only an administrator can act. */}
        <div className="flex shrink-0 items-center gap-2">
          {!skipped && view.action && (
            <Button
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={onAct}
            >
              {busy && (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              )}
              {label}
            </Button>
          )}
          {canSkip && (
            <Button
              size="sm"
              variant="ghost"
              disabled={disabled}
              onClick={onToggleSkip}
            >
              {skipped ? 'Undo' : 'Skip'}
            </Button>
          )}
        </div>
      </div>
      {error && !skipped && (
        <p role="alert" className="text-[13px] text-destructive">
          {error}
        </p>
      )}
      {!skipped && children}
    </div>
  );
}

/**
 * The words to send to the person who can approve Teams Phone, ready to copy.
 * Shown outright for someone who cannot connect it, and offered as a button for
 * an admin who might not be a Microsoft 365 global administrator.
 */
function AdminRequest({
  show,
  offerCopy,
}: {
  show: boolean;
  offerCopy: boolean;
}) {
  const text = adminRequestText(window.location.origin);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied. Send it to your administrator.');
    } catch {
      toast.error('Could not copy. Select the text and copy it.');
    }
  };
  if (!show && !offerCopy) return null;
  if (!show) {
    return (
      <Button
        size="sm"
        variant="ghost"
        className="px-0 text-muted-foreground"
        onClick={() => void copy()}
      >
        <Copy className="size-3.5" aria-hidden="true" />
        Copy a request for your administrator
      </Button>
    );
  }
  return (
    <div className="space-y-2">
      <p className="text-[13px] text-muted-foreground">
        Teams Phone needs a Microsoft 365 global administrator to approve it for
        your whole firm. Send them this:
      </p>
      <textarea
        readOnly
        aria-label="Request for your administrator"
        value={text}
        rows={4}
        className="w-full resize-none rounded-md border bg-muted/40 p-2 text-[13px]"
        onFocus={(e) => e.currentTarget.select()}
      />
      <Button size="sm" variant="outline" onClick={() => void copy()}>
        <Copy className="size-3.5" aria-hidden="true" />
        Copy
      </Button>
    </div>
  );
}
